import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Trash2, Download, FileText, Info, Plus, Pencil, Check, X } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { GRUPPI_MANSIONI } from '../../utils/mansioni';

// per quali mansioni è pensato il template (nessuna spunta = tutte): nella scheda allenatore il
// template giusto viene proposto da solo (12/10/2026)
function ScegliMansioni({ value, onChange }) {
  const set = new Set(value || []);
  const cambia = (m) => { const n = new Set(set); n.has(m) ? n.delete(m) : n.add(m); onChange([...n]); };
  return (
    <div>
      <div style={{ fontSize: 'var(--font-size-xs)', color: '#777', marginBottom: '6px' }}>{set.size ? `Solo per: ${[...set].join(', ')}` : 'Per tutte le mansioni (nessuna spunta)'}{set.size > 0 && <button type="button" onClick={() => onChange([])} style={{ marginLeft: '8px', background: 'none', border: 'none', color: '#F4600C', cursor: 'pointer', fontSize: 'var(--font-size-xs)', textDecoration: 'underline' }}>togli tutte</button>}</div>
      {GRUPPI_MANSIONI.map(([g, voci]) => (
        <div key={g} style={{ marginBottom: '6px' }}>
          <div style={{ fontSize: '11px', color: '#888', fontWeight: 700, marginBottom: '3px' }}>{g}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {voci.map(m => <button type="button" key={m} onClick={() => cambia(m)} style={{ border: `1px solid ${set.has(m) ? '#F4600C' : 'var(--border2,#2A2A2A)'}`, background: set.has(m) ? '#F4600C20' : 'transparent', color: set.has(m) ? '#F4600C' : 'var(--text-muted,#888)', borderRadius: '999px', padding: '3px 10px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>{m}</button>)}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function GestioneTemplate() {
  const { user } = useAuth();
  const puoCreare     = puoFare(user, 'dipendenti', 'create');
  const puoModificare = puoFare(user, 'dipendenti', 'edit');
  const puoEliminare  = puoFare(user, 'dipendenti', 'delete');
  const navigate = useNavigate();
  const fileRefOrario = useRef(null);
  const fileRefFisso  = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [uploadingOrario, setUploadingOrario] = useState(false);
  const [uploadingFisso, setUploadingFisso]   = useState(false);
  const [formOrario, setFormOrario] = useState({ nome: '', anno: new Date().getFullYear(), ruoli: [] });
  const [formFisso, setFormFisso]   = useState({ nome: '', anno: new Date().getFullYear(), ruoli: [] });
  const [ruoliEdit, setRuoliEdit]   = useState(null); // { id, ruoli }

  const [variabili, setVariabili]       = useState([]);
  const [loadingVar, setLoadingVar]     = useState(true);
  const [nuovaVar, setNuovaVar]         = useState({ chiave: '', valore: '', descrizione: '' });
  const [editVarId, setEditVarId]       = useState(null);
  const [editVarDraft, setEditVarDraft] = useState({ valore: '', descrizione: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/dipendenti/contratti/template');
      setTemplates(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const loadVariabili = useCallback(async () => {
    setLoadingVar(true);
    try {
      const r = await api.get('/dipendenti/variabili-contratto');
      setVariabili(r.data);
    } catch(e) { console.error(e); }
    finally { setLoadingVar(false); }
  }, []);

  useEffect(() => { load(); loadVariabili(); }, [load, loadVariabili]);

  const creaVariabile = async () => {
    if (!nuovaVar.chiave.trim()) return alert('Inserisci il nome della variabile');
    try {
      await api.post('/dipendenti/variabili-contratto', nuovaVar);
      setNuovaVar({ chiave: '', valore: '', descrizione: '' });
      loadVariabili();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const iniziaModificaVar = (v) => { setEditVarId(v.id); setEditVarDraft({ valore: v.valore, descrizione: v.descrizione || '' }); };
  const annullaModificaVar = () => setEditVarId(null);

  const salvaModificaVar = async (id) => {
    try {
      await api.put(`/dipendenti/variabili-contratto/${id}`, editVarDraft);
      setEditVarId(null);
      loadVariabili();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const eliminaVariabile = async (id, chiave) => {
    if (!window.confirm(`Eliminare la variabile "${chiave}"?`)) return;
    try {
      await api.delete(`/dipendenti/variabili-contratto/${id}`);
      loadVariabili();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const handleUpload = async (tipo) => {
    const isFisso  = tipo === 'FISSO';
    const fileRef  = isFisso ? fileRefFisso : fileRefOrario;
    const form     = isFisso ? formFisso : formOrario;
    const setForm  = isFisso ? setFormFisso : setFormOrario;
    const setUploading = isFisso ? setUploadingFisso : setUploadingOrario;

    const file = fileRef.current?.files?.[0];
    if (!file) return alert('Seleziona un file .docx');
    if (!file.name.endsWith('.docx')) return alert('Solo file .docx sono supportati');
    if (!form.nome.trim()) return alert('Inserisci un nome per il template');

    setUploading(true);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post('/dipendenti/contratti/template', {
        fileData: base64,
        fileName: file.name,
        nome:     form.nome.trim(),
        anno:     form.anno,
        tipo,
        ruoli:    form.ruoli,
      });
      fileRef.current.value = '';
      setForm(p => ({ ...p, nome: '', ruoli: [] }));
      load();
    } catch(e) {
      alert('Errore upload: ' + (e.response?.data?.error || e.message));
    } finally {
      setUploading(false);
    }
  };

  const salvaRuoli = async () => {
    try {
      await api.put(`/dipendenti/contratti/template/${ruoliEdit.id}/ruoli`, { ruoli: ruoliEdit.ruoli });
      setRuoliEdit(null);
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const elimina = async (id, nome) => {
    if (!window.confirm(`Eliminare il template "${nome}"?`)) return;
    try {
      await api.delete(`/dipendenti/contratti/template/${id}`);
      load();
    } catch(e) { alert('Errore: ' + (e.response?.data?.error || e.message)); }
  };

  const scarica = async (id, fileName) => {
    try {
      const r = await api.get(`/dipendenti/contratti/template/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
    } catch(e) { alert('Errore download: ' + (e.response?.data?.error || e.message)); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:'12px', marginBottom:'24px' }}>
        <button style={s.btnSec} onClick={() => navigate('/dipendenti')}>
          <ArrowLeft size={15}/>
        </button>
        <div>
          <h1 style={s.title}>TEMPLATE CONTRATTI</h1>
          <p style={s.sub}>Gestione template .docx per generazione automatica contratti e autocertificazioni</p>
        </div>
      </div>

      {/* Box variabili disponibili */}
      <div style={{ background:'#F4600C10', border:'1px solid #F4600C30', borderRadius:'12px', padding:'16px', marginBottom:'24px' }}>
        <div style={{ display:'flex', gap:'8px', alignItems:'center', marginBottom:'10px' }}>
          <Info size={14} color="#F4600C"/>
          <span style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#F4600C' }}>VARIABILI DISPONIBILI NEL TEMPLATE .DOCX</span>
        </div>
        <p style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginBottom:'12px' }}>
          Inserisci queste variabili nel tuo documento Word tra doppie parentesi graffe, es. <code style={{ color:'#F4600C' }}>{`{{nome}}`}</code>. Sono fisse (di sistema) — per aggiungerne di tue vedi la sezione qui sotto.
        </p>
        <div style={{ display:'flex', gap:'20px', flexWrap:'wrap' }}>
          {[
            ['DIPENDENTE', [
              ['{{nome}}',               'Nome'],
              ['{{cognome}}',            'Cognome'],
              ['{{nome_cognome}}',       'Nome e Cognome'],
              ['{{cognome_nome}}',       'Cognome e Nome'],
              ['{{codice_fiscale}}',     'Codice fiscale'],
              ['{{data_nascita}}',       'Data di nascita'],
              ['{{luogo_nascita}}',      'Luogo di nascita'],
              ['{{provincia_nascita}}',  'Provincia di nascita (es. MI)'],
              ['{{email}}',              'Email'],
              ['{{telefono}}',           'Telefono'],
            ]],
            ['INDIRIZZO', [
              ['{{indirizzo_residenza}}','Indirizzo residenza'],
              ['{{cap}}',                'CAP'],
              ['{{paese}}',              'Paese'],
              ['{{provincia}}',          'Provincia (es. MI)'],
              ['{{indirizzo_completo}}', 'Indirizzo + CAP + Paese + Prov.'],
            ]],
            ['CONTRATTO', [
              ['{{ruolo}}',              'Mansione in minuscolo, per la frase (es. "quale {{ruolo}} di Beach Volley")'],
              ['{{Ruolo}}',              'Mansione con la maiuscola (titoli, tabelle)'],
              ['{{ruolo_specifica}}',    'Specifica facoltativa (es. 2° grado beach volley)'],
              ['{{ruolo_completo}}',     'Mansione + specifica tra parentesi'],
              ['{{modalita_compenso}}',  'Orario o Fisso'],
              ['{{tariffa_oraria}}',     'Tariffa oraria (€)'],
              ['{{compenso_fisso}}',     'Compenso fisso mensile (€)'],
              ['{{data_inizio}}',        'Data inizio rapporto'],
              ['{{dipendente_pubblico}}',      'Sì o No'],
              ['{{x_dipendente_pubblico}}',    'X se dipendente pubblico, altrimenti vuoto'],
              ['{{x_non_dipendente_pubblico}}','X se NON dipendente pubblico, altrimenti vuoto'],
              ['{{anno}}',               'Anno contratto'],
              ['{{data_oggi}}',          'Data odierna'],
            ]],
            ['ASD (ASSOCIAZIONE)', [
              ['{{asd_nome}}',           'Nome ASD'],
              ['{{asd_cf}}',             'Codice fiscale ASD'],
              ['{{asd_piva}}',           'Partita IVA ASD'],
              ['{{asd_indirizzo}}',      'Indirizzo ASD'],
              ['{{asd_citta}}',          'Città ASD'],
              ['{{asd_email}}',          'Email ASD'],
            ]],
          ].map(([gruppo, voci]) => (
            <div key={gruppo} style={{ minWidth:'220px' }}>
              <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'1px', color:'#999', marginBottom:'6px', borderBottom:'1px solid #F4600C30', paddingBottom:'4px' }}>
                {gruppo}
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', columnGap:'10px', rowGap:'3px' }}>
                {voci.map(([v, desc]) => (
                  <React.Fragment key={v}>
                    <code style={{ color:'#F4600C', fontSize: 'var(--font-size-xs)', whiteSpace:'nowrap' }}>{v}</code>
                    <span style={{ color:'#888', fontSize: 'var(--font-size-xs)' }}>{desc}</span>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Variabili personalizzate */}
      <div style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'12px', padding:'16px', marginBottom:'24px' }}>
        <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:'#555', marginBottom:'4px' }}>VARIABILI PERSONALIZZATE</div>
        <p style={{ fontSize: 'var(--font-size-xs)', color:'#888', marginBottom:'12px' }}>
          Aggiungi le tue variabili fisse (uguali per tutti i contratti finché non le cambi), utilizzabili nel .docx come <code style={{ color:'#F4600C' }}>{`{{chiave}}`}</code> — es. chiave <code style={{ color:'#F4600C' }}>durata_prova</code>, valore <code>3 mesi</code>.
        </p>

        {loadingVar ? (
          <div style={{ padding:'16px', color:'#555', textAlign:'center', fontSize: 'var(--font-size-xs)' }}>Caricamento...</div>
        ) : variabili.length === 0 ? (
          <div style={{ padding:'8px 0 16px', color:'#555', fontSize: 'var(--font-size-xs)' }}>Nessuna variabile personalizzata ancora aggiunta.</div>
        ) : (
          <div style={{ display:'flex', flexDirection:'column', gap:'6px', marginBottom:'16px' }}>
            {variabili.map(v => (
              <div key={v.id} style={{ display:'flex', alignItems:'center', gap:'10px', background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'8px', padding:'8px 12px' }}>
                <code style={{ color:'#F4600C', fontSize: 'var(--font-size-xs)', minWidth:'150px', flexShrink:0 }}>{`{{${v.chiave}}}`}</code>
                {editVarId === v.id ? (
                  <>
                    <input style={{ ...s.inp, flex:1 }} placeholder="Valore" value={editVarDraft.valore} onChange={e=>setEditVarDraft(p=>({...p,valore:e.target.value}))} autoFocus/>
                    <input style={{ ...s.inp, flex:1 }} placeholder="Descrizione (opzionale)" value={editVarDraft.descrizione} onChange={e=>setEditVarDraft(p=>({...p,descrizione:e.target.value}))}/>
                    <button style={s.btnSec} onClick={() => salvaModificaVar(v.id)} title="Salva"><Check size={13}/></button>
                    <button style={s.btnSec} onClick={annullaModificaVar} title="Annulla"><X size={13}/></button>
                  </>
                ) : (
                  <>
                    <span style={{ flex:1, fontSize: 'var(--font-size-sm)' }}>{v.valore}</span>
                    <span style={{ flex:1, fontSize: 'var(--font-size-xs)', color:'#888' }}>{v.descrizione}</span>
                    {puoModificare && <button style={s.btnSec} onClick={() => iniziaModificaVar(v)} title="Modifica"><Pencil size={13}/></button>}
                    {puoEliminare && <button style={{ ...s.btnSec, color:'#EF4444', borderColor:'#EF444440' }} onClick={() => eliminaVariabile(v.id, v.chiave)} title="Elimina"><Trash2 size={13}/></button>}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {puoCreare && <div style={{ display:'flex', gap:'10px', alignItems:'flex-end', flexWrap:'wrap' }}>
          <div style={{ flex:1, minWidth:'140px' }}>
            <label style={s.label}>NOME VARIABILE *</label>
            <input style={s.inp} placeholder="es. durata_prova" value={nuovaVar.chiave} onChange={e=>setNuovaVar(p=>({...p,chiave:e.target.value}))}/>
          </div>
          <div style={{ flex:1, minWidth:'140px' }}>
            <label style={s.label}>VALORE</label>
            <input style={s.inp} placeholder="es. 3 mesi" value={nuovaVar.valore} onChange={e=>setNuovaVar(p=>({...p,valore:e.target.value}))}/>
          </div>
          <div style={{ flex:1, minWidth:'140px' }}>
            <label style={s.label}>DESCRIZIONE</label>
            <input style={s.inp} placeholder="opzionale" value={nuovaVar.descrizione} onChange={e=>setNuovaVar(p=>({...p,descrizione:e.target.value}))}/>
          </div>
          <button style={s.btnPri} onClick={creaVariabile}>
            <Plus size={13}/> AGGIUNGI
          </button>
        </div>}
      </div>

      {/* Upload + lista, separati per modalità di compenso */}
      {[
        { tipo:'ORARIO', titolo:'CONTRATTI A TARIFFA ORARIA', colore:'#F4600C',
          form:formOrario, setForm:setFormOrario, fileRef:fileRefOrario, uploading:uploadingOrario },
        { tipo:'FISSO',  titolo:'CONTRATTI A COMPENSO FISSO', colore:'#8B5CF6',
          form:formFisso, setForm:setFormFisso, fileRef:fileRefFisso, uploading:uploadingFisso },
      ].map(({ tipo, titolo, colore, form, setForm, fileRef, uploading }) => {
        const templatesTipo = templates.filter(t => (t.tipo || 'ORARIO') === tipo);
        return (
          <div key={tipo} style={{ marginBottom:'24px' }}>
            <div style={{ fontSize: 'var(--font-size-xs)', fontWeight:'700', letterSpacing:'2px', color:colore, marginBottom:'10px' }}>{titolo}</div>

            {/* Upload */}
            {puoCreare && <div style={{ background:'var(--bg-card,#141414)', border:`1px solid ${colore}30`, borderRadius:'12px', padding:'16px', marginBottom:'10px' }}>
              <div style={{ display:'flex', gap:'10px', alignItems:'flex-end', flexWrap:'wrap' }}>
                <div style={{ flex:2, minWidth:'180px' }}>
                  <label style={s.label}>NOME TEMPLATE *</label>
                  <input style={s.inp} placeholder={tipo==='FISSO' ? 'es. Contratto compenso fisso 2026' : 'es. Contratto Co.Co.Co. 2026'} value={form.nome} onChange={e=>setForm(p=>({...p,nome:e.target.value}))}/>
                </div>
                <div style={{ flex:1, minWidth:'90px' }}>
                  <label style={s.label}>ANNO</label>
                  <input style={s.inp} type="number" value={form.anno} onChange={e=>setForm(p=>({...p,anno:+e.target.value}))} min={2020} max={2040}/>
                </div>
                <div style={{ flex:2, minWidth:'180px' }}>
                  <label style={s.label}>FILE .DOCX *</label>
                  <input ref={fileRef} type="file" accept=".docx" style={{ ...s.inp, padding:'6px 10px', cursor:'pointer' }}/>
                </div>
                <button style={{ ...s.btnPri, background:colore }} onClick={() => handleUpload(tipo)} disabled={uploading}>
                  <Upload size={13}/> {uploading ? 'CARICAMENTO...' : 'CARICA'}
                </button>
              </div>
              <div style={{ marginTop:'12px' }}>
                <label style={s.label}>PER QUALI MANSIONI</label>
                <ScegliMansioni value={form.ruoli} onChange={r=>setForm(p=>({...p,ruoli:r}))}/>
              </div>
            </div>}

            {/* Lista template di questo tipo */}
            {loading ? (
              <div style={{ padding:'20px', color:'#555', textAlign:'center' }}>Caricamento...</div>
            ) : templatesTipo.length === 0 ? (
              <div style={{ padding:'20px', color:'#555', textAlign:'center', fontSize: 'var(--font-size-sm)' }}>
                Nessun template {tipo==='FISSO' ? 'a compenso fisso' : 'a tariffa oraria'} caricato.
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
                {templatesTipo.map(t => (
                  <div key={t.id} style={{ background:'var(--bg-card,#141414)', border:'1px solid var(--border,#1E1E1E)', borderRadius:'10px', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                    <FileText size={18} color={colore}/>
                    <div style={{ flex:1, minWidth:'200px' }}>
                      <div style={{ fontSize: 'var(--font-size-sm)', fontWeight:'700' }}>{t.nome}</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color:'#555' }}>Anno {t.anno} · Caricato il {new Date(t.created_at).toLocaleDateString('it-IT')}</div>
                      <div style={{ fontSize: 'var(--font-size-xs)', color:'#777', marginTop:'2px' }}>
                        Mansioni: <b>{t.ruoli?.length ? t.ruoli.join(', ') : 'tutte'}</b>
                        {puoModificare && ruoliEdit?.id !== t.id && <button type="button" onClick={() => setRuoliEdit({ id: t.id, ruoli: t.ruoli || [] })} style={{ marginLeft:'8px', background:'none', border:'none', color:'#F4600C', cursor:'pointer', fontSize: 'var(--font-size-xs)', textDecoration:'underline' }}>cambia</button>}
                      </div>
                      {ruoliEdit?.id === t.id && (
                        <div style={{ marginTop:'10px' }}>
                          <ScegliMansioni value={ruoliEdit.ruoli} onChange={r => setRuoliEdit({ ...ruoliEdit, ruoli: r })}/>
                          <div style={{ display:'flex', gap:'6px', marginTop:'6px' }}>
                            <button style={s.btnPri} onClick={salvaRuoli}><Check size={13}/> SALVA</button>
                            <button style={s.btnSec} onClick={() => setRuoliEdit(null)}><X size={13}/> ANNULLA</button>
                          </div>
                        </div>
                      )}
                    </div>
                    <button style={s.btnSec} onClick={() => scarica(t.id, t.file_nome)} title="Scarica template">
                      <Download size={13}/>
                    </button>
                    {puoEliminare && <button style={{ ...s.btnSec, color:'#EF4444', borderColor:'#EF444440' }} onClick={() => elimina(t.id, t.nome)} title="Elimina template">
                      <Trash2 size={13}/>
                    </button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

const s = {
  title:   { fontFamily:"'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight:'800', letterSpacing:'2px' },
  sub:     { color:'var(--text-muted,#888)', fontSize: 'var(--font-size-xs)', marginTop:'2px' },
  btnPri:  { display:'flex', alignItems:'center', gap:'6px', background:'#F4600C', color:'#fff', border:'none', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer', whiteSpace:'nowrap' },
  btnSec:  { display:'flex', alignItems:'center', gap:'6px', background:'var(--bg-input,#1A1A1A)', color:'var(--text,#fff)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'9px 14px', fontSize: 'var(--font-size-xs)', fontWeight:'700', cursor:'pointer' },
  label:   { fontSize: 'var(--font-size-xs)', letterSpacing:'2px', fontWeight:'700', color:'#555', display:'block', marginBottom:'4px' },
  inp:     { background:'var(--bg-input,#1A1A1A)', border:'1px solid var(--border2,#2A2A2A)', borderRadius:'8px', padding:'8px 12px', color:'var(--text,#fff)', fontSize: 'var(--font-size-sm)', outline:'none', width:'100%', boxSizing:'border-box' },
};
